/**
 * PerTenorEditor — a small add/remove table for a `per_tenor` price ladder
 * (ADR-0014). Each row is one offered tenor (in months) and its financed
 * total. Unit-neutral: the `total` strings are stored verbatim by the parent,
 * which decides whether they are minor or major units and converts at submit.
 *
 * Shared by the create form (minor units) and the edit dialog (major units),
 * so it carries no money parsing of its own — it's purely the row editor.
 */
import { Button } from "./Button.js";

export interface PerTenorRow {
  readonly tenorMonths: string;
  readonly total: string;
}

/** A fresh, empty ladder row. */
export function emptyTenorRow(): PerTenorRow {
  return { tenorMonths: "", total: "" };
}

export function PerTenorEditor({
  rows,
  onChange,
  totalLabel,
}: {
  readonly rows: readonly PerTenorRow[];
  readonly onChange: (rows: readonly PerTenorRow[]) => void;
  /** Column header for the total field, e.g. "Total (minor)" or "Total (TZS)". */
  readonly totalLabel: string;
}): JSX.Element {
  function setRow(i: number, patch: Partial<PerTenorRow>): void {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow(): void {
    onChange([...rows, emptyTenorRow()]);
  }
  function removeRow(i: number): void {
    onChange(rows.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_2fr_auto] gap-2 text-[10px] font-bold uppercase tracking-wider text-simuflex-slate">
        <span>Tenor (months)</span>
        <span>{totalLabel}</span>
        <span className="w-8" />
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-simuflex-slate">No tenors yet — add at least one.</p>
      ) : (
        rows.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
            <input
              inputMode="numeric"
              value={row.tenorMonths}
              onChange={(e) => setRow(i, { tenorMonths: e.target.value })}
              placeholder="e.g. 4"
              className="block w-full rounded-lg border border-simuflex-line bg-white px-3 py-2 text-sm text-simuflex-navy"
            />
            <input
              inputMode="numeric"
              value={row.total}
              onChange={(e) => setRow(i, { total: e.target.value })}
              placeholder="financed total"
              className="block w-full rounded-lg border border-simuflex-line bg-white px-3 py-2 text-sm text-simuflex-navy"
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              title="Remove tenor"
              className="px-2 py-1 text-xs font-bold text-simuflex-orange hover:text-red-700"
            >
              ✕
            </button>
          </div>
        ))
      )}
      <Button type="button" variant="ghost" onClick={addRow}>
        + Add tenor
      </Button>
    </div>
  );
}
